import { applyPatch } from 'fast-json-patch';
import { fetchResource } from './resourceApi';
import { getChatSessionDetails } from './chatSessionApi';

const resourceSyncDrivers = new Map();

const defaultResourceSyncDriver = {
  async handleCreated(currentMap, content) {
    try {
      const newResource = await fetchResource(content.resource_type, content.resource_id);
      if (!newResource) {
        console.error(`[ResourceSync] Failed to fetch newly created resource ${content.resource_id}.`);
        return currentMap;
      }
      if (!newResource.type) {
        newResource.type = newResource.resource_type;
      }
      // O(1) update
      return { ...currentMap, [newResource.id]: newResource };
    } catch (error) {
      console.error(`[ResourceSync] Error fetching created resource ${content.resource_id}:`, error);
      return currentMap;
    }
  },

  async handleUpdated(currentMap, content) {
    let finalResource;
    if (!content.value) {
      console.warn(`[ResourceSync] 'updated' event for ${content.resource_type}:${content.resource_id} is missing 'value'. Fetching full object.`);
      try {
        let updatedResource;
        if (content.resource_type === 'chat_session') {
          updatedResource = await getChatSessionDetails(content.resource_id);
        } else {
          updatedResource = await fetchResource(content.resource_type, content.resource_id);
        }
        if (!updatedResource) return currentMap;
        finalResource = updatedResource;
      } catch (error) {
        return currentMap;
      }
    } else {
      finalResource = content.value;
    }

    if (!finalResource.type) finalResource.type = content.resource_type;
    if (!finalResource.resource_type) finalResource.resource_type = content.resource_type;

    // O(1) update
    return { ...currentMap, [content.resource_id]: finalResource };
  },

  async handlePatched(currentMap, content) {
    const resourceToPatch = currentMap[content.resource_id];
    
    if (!resourceToPatch) {
      console.warn(`[ResourceSync] Cannot apply patch: Resource ${content.resource_id} not found in state. Fetching full resource.`);
      return await this.handleCreated(currentMap, content);
    }

    try {
      const patchedResource = applyPatch(resourceToPatch, content.patches, true, false).newDocument;
      if (!patchedResource.type) patchedResource.type = content.resource_type;
      
      // O(1) update
      return { ...currentMap, [content.resource_id]: patchedResource };
    } catch (e) {
      console.warn(`[ResourceSync] Patch failed for ${content.resource_id}. Fetching full resource.`);
      return await this.handleCreated(currentMap, content);
    }
  },

  handleDeleted(currentMap, content) {
    const newMap = { ...currentMap };
    delete newMap[content.resource_id];
    return newMap;
  },
};

export const registerResourceSyncDriver = (resourceType, driver) => {
  const mergedDriver = { ...defaultResourceSyncDriver, ...driver };
  resourceSyncDrivers.set(resourceType, mergedDriver);
};

export const applyResourceSyncEvent = async (currentMap, event) => {
  if (event.msg_type !== 'RESOURCE_SYNC' || !event.content) {
    return currentMap;
  }
  const { content } = event;
  const { resource_type, change_type } = content;
  const driver = resourceSyncDrivers.get(resource_type) || defaultResourceSyncDriver;
  
  // Ensure we are working with an object
  const safeCurrentMap = currentMap || {};

  switch (change_type) {
    case 'created':
      return await driver.handleCreated(safeCurrentMap, content);
    case 'updated':
      return await driver.handleUpdated(safeCurrentMap, content);
    case 'patched':
      return await driver.handlePatched(safeCurrentMap, content);
    case 'deleted':
      return driver.handleDeleted(safeCurrentMap, content);
    default:
      console.warn(`[ResourceSync] Unknown change_type: ${change_type}`);
      return safeCurrentMap;
  }
};